// src/products/dataroom/services/wizardUsersService.js
import { supabase } from '../../../config';

const wizardUsersService = {
  // Get users created through the setup wizard across all projects (for global users page)
  async getAllWizardUsers() {
    try {
      console.log('wizardUsersService.getAllWizardUsers called');
      
      // First check if there are any parties at all
      const { data: partiesCheck, error: partiesCheckError } = await supabase
        .from('dataroom_parties')
        .select('id')
        .limit(1);
      
      if (partiesCheckError) {
        console.log('No parties found or error:', partiesCheckError);
        return { data: [], error: null };
      }
      
      if (!partiesCheck || partiesCheck.length === 0) {
        console.log('No parties found in database');
        return { data: [], error: null };
      }
      
      // Get all dataroom users (including pending ones with email only)
      const { data: dataroomUsers, error } = await supabase
        .from('dataroom_users')
        .select('user_id, email, project_id, role, status, invited_at, last_active, metadata');

      if (error) {
        console.log('Error fetching dataroom users:', error);
        return { data: [], error: null };
      }

      if (!dataroomUsers || dataroomUsers.length === 0) {
        console.log('No dataroom users found');
        return { data: [], error: null };
      }

      console.log('=== WIZARD USERS SERVICE DEBUG ===');
      console.log('Found dataroom users:', dataroomUsers.length, 'users');
      console.log('First user raw data:', dataroomUsers[0]);
      console.log('All users emails:', dataroomUsers.map(u => u.email || u.user_id));

      // Get unique party IDs from metadata (convert to integers)
      const partyIds = [...new Set(
        dataroomUsers
          .filter(u => u.metadata?.party_id)
          .map(u => parseInt(u.metadata.party_id))
          .filter(id => !isNaN(id))
      )];
      
      console.log('Party IDs to fetch:', partyIds);
      
      // Fetch party details
      const { data: partiesData, error: partiesError } = await supabase
        .from('dataroom_parties')
        .select('id, name, code, color, project_id')
        .in('id', partyIds);

      if (partiesError) {
        console.log('Error fetching parties:', partiesError);
      } else {
        console.log('Fetched parties:', partiesData);
      }

      // Get unique team IDs from metadata (convert to integers)
      const teamIds = [...new Set(
        dataroomUsers
          .filter(u => u.metadata?.team_id)
          .map(u => parseInt(u.metadata.team_id))
          .filter(id => !isNaN(id))
      )];
      
      console.log('Team IDs to fetch:', teamIds);
      
      // Fetch team details if there are teams
      let teamsData = [];
      if (teamIds.length > 0) {
        const { data: tData } = await supabase
          .from('dataroom_teams')
          .select('id, name, code, party_id')
          .in('id', teamIds);
        teamsData = tData || [];
        console.log('Fetched teams:', teamsData);
      }

      // Create maps for quick lookup
      const partiesMap = new Map(partiesData?.map(p => [p.id, p]) || []);
      const teamsMap = new Map(teamsData?.map(t => [t.id, t]) || []);
      
      console.log('Parties map:', partiesMap);
      console.log('Teams map:', teamsMap);

      // Get invite status for each email
      const emails = [...new Set(dataroomUsers.map(u => u.email).filter(Boolean))];
      const { data: invites } = await supabase
        .from('userinvites_invites')
        .select('email, invite_status, sentat, acceptedat')
        .in('email', emails)
        .eq('inviteforproduct', 'dataroom');

      // Create a map of email to invite status
      const inviteStatusMap = new Map();
      invites?.forEach(invite => {
        if (!inviteStatusMap.has(invite.email) || invite.sentat > inviteStatusMap.get(invite.email).sentat) {
          inviteStatusMap.set(invite.email, {
            status: invite.invite_status === 'accepted' ? 'active' : 'invited',
            sentAt: invite.sentat,
            acceptedAt: invite.acceptedat
          });
        }
      });

      // Get user profiles for users with user_id
      const userIds = dataroomUsers
        .map(u => u.user_id)
        .filter(id => id && typeof id === 'string' && id.includes('-')) // UUID format
        .filter(Boolean);
      
      let userProfilesMap = new Map();
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('user_profiles')
          .select('id, email, first_name, last_name, full_name')
          .in('id', userIds);
        
        if (!profilesError && profiles) {
          profiles.forEach(profile => {
            userProfilesMap.set(profile.id, profile);
          });
        }
      }

      // Transform dataroom_users to user objects
      const users = dataroomUsers.map(dataroomUser => {
        const email = dataroomUser.email || dataroomUser.user_id;
        const metadata = dataroomUser.metadata || {};
        const inviteStatus = inviteStatusMap.get(email);
        
        // Get party and team info from metadata (convert IDs to integers for lookup)
        const partyId = metadata.party_id ? parseInt(metadata.party_id) : null;
        const teamId = metadata.team_id ? parseInt(metadata.team_id) : null;
        
        const party = partyId ? partiesMap.get(partyId) : null;
        const team = teamId ? teamsMap.get(teamId) : null;
        
        // Get user profile if available
        const userProfile = dataroomUser.user_id ? userProfilesMap.get(dataroomUser.user_id) : null;
        
        // Determine full name: priority: metadata > user_profile > email prefix
        let fullName = '';
        if (metadata.first_name && metadata.last_name) {
          fullName = `${metadata.first_name} ${metadata.last_name}`;
        } else if (metadata.first_name) {
          fullName = metadata.first_name;
        } else if (userProfile) {
          // Use profile name if available
          if (userProfile.full_name) {
            fullName = userProfile.full_name;
          } else if (userProfile.first_name && userProfile.last_name) {
            fullName = `${userProfile.first_name} ${userProfile.last_name}`;
          } else if (userProfile.first_name || userProfile.last_name) {
            fullName = userProfile.first_name || userProfile.last_name;
          } else {
            fullName = email.split('@')[0];
          }
        } else {
          fullName = email.split('@')[0];
        }
        
        return {
          id: `${email}-${dataroomUser.project_id}`, // Composite key: email-projectId to handle same user in multiple projects
          email: email,
          projectId: dataroomUser.project_id, // Include project_id for reference
          fullName: fullName,
          firstName: metadata.first_name || userProfile?.first_name || '',
          lastName: metadata.last_name || userProfile?.last_name || '',
          role: dataroomUser.role || 'viewer',
          status: inviteStatus?.status || dataroomUser.status || 'pending',
          invitedAt: inviteStatus?.sentAt || dataroomUser.invited_at,
          acceptedAt: inviteStatus?.acceptedAt,
          lastActive: dataroomUser.last_active,
          avatarUrl: null,
          parties: party ? [{
          id: party.id,
          name: party.name,
          code: party.code,
            color: party.color,
            projectId: party.project_id
          }] : [],
          teams: team ? [{
            id: team.id,
            name: team.name,
            code: team.code,
            partyId: team.party_id
          }] : []
        };
      });

      console.log('=== TRANSFORMED USERS ===');
      console.log('Total users after transform:', users.length);
      if (users.length > 0) {
        console.log('Sample transformed user:', users[0]);
        console.log('First user parties:', users[0].parties);
        console.log('First user teams:', users[0].teams);
      }
      
      return { data: users, error: null };
    } catch (error) {
      console.error('Error fetching all wizard users:', error);
      return { data: null, error };
    }
  },

  // Get users created through the setup wizard for a project
  async getWizardUsers(projectId) {
    try {
      console.log('wizardUsersService.getWizardUsers called with projectId:', projectId);
      // Get dataroom users for this project (including pending ones with email only)
      const { data: dataroomUsers, error } = await supabase
        .from('dataroom_users')
        .select('user_id, email, project_id, role, status, invited_at, last_active, metadata')
        .eq('project_id', projectId);

      if (error) throw error;

      if (!dataroomUsers || dataroomUsers.length === 0) {
        return { data: [], error: null };
      }

      // Get unique party IDs from metadata (convert to integers)
      const partyIds = [...new Set(
        dataroomUsers
          .filter(u => u.metadata?.party_id)
          .map(u => parseInt(u.metadata.party_id))
          .filter(id => !isNaN(id))
      )];
      
      // Fetch party details
      const { data: partiesData, error: partiesError } = await supabase
        .from('dataroom_parties')
        .select('id, name, code, color, project_id')
        .in('id', partyIds);

      if (partiesError) {
        console.log('Error fetching parties:', partiesError);
      }

      // Get unique team IDs from metadata (convert to integers)
      const teamIds = [...new Set(
        dataroomUsers
          .filter(u => u.metadata?.team_id)
          .map(u => parseInt(u.metadata.team_id))
          .filter(id => !isNaN(id))
      )];
      
      // Fetch team details if there are teams
      let teamsData = [];
      if (teamIds.length > 0) {
        const { data: tData } = await supabase
          .from('dataroom_teams')
          .select('id, name, code, party_id')
          .in('id', teamIds);
        teamsData = tData || [];
      }

      // Create maps for quick lookup
      const partiesMap = new Map(partiesData?.map(p => [p.id, p]) || []);
      const teamsMap = new Map(teamsData?.map(t => [t.id, t]) || []);

      // Get invite status for each email
      const emails = [...new Set(dataroomUsers.map(u => u.email).filter(Boolean))];
      const { data: invites } = await supabase
        .from('userinvites_invites')
        .select('email, invite_status, sentat, acceptedat')
        .in('email', emails)
        .eq('inviteforproduct', 'dataroom')
        .eq('projectid', projectId);

      // Create a map of email to invite status
      const inviteStatusMap = new Map();
      invites?.forEach(invite => {
        if (!inviteStatusMap.has(invite.email) || invite.sentat > inviteStatusMap.get(invite.email).sentat) {
          inviteStatusMap.set(invite.email, {
            status: invite.invite_status === 'accepted' ? 'active' : 'invited',
            sentAt: invite.sentat,
            acceptedAt: invite.acceptedat
          });
        }
      });

      // Get user profiles for users with user_id
      const userIds = dataroomUsers
        .map(u => u.user_id)
        .filter(id => id && typeof id === 'string' && id.includes('-')) // UUID format
        .filter(Boolean);
      
      let userProfilesMap = new Map();
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('user_profiles')
          .select('id, email, first_name, last_name, full_name')
          .in('id', userIds);
        
        if (!profilesError && profiles) {
          profiles.forEach(profile => {
            userProfilesMap.set(profile.id, profile);
          });
        }
      }

      // Transform dataroom_users to user objects
      const users = dataroomUsers.map(dataroomUser => {
        const email = dataroomUser.email || dataroomUser.user_id;
        const metadata = dataroomUser.metadata || {};
        const inviteStatus = inviteStatusMap.get(email);
        
        // Get party and team info from metadata (convert IDs to integers for lookup)
        const partyId = metadata.party_id ? parseInt(metadata.party_id) : null;
        const teamId = metadata.team_id ? parseInt(metadata.team_id) : null;
        
        const party = partyId ? partiesMap.get(partyId) : null;
        const team = teamId ? teamsMap.get(teamId) : null;
        
        // Get user profile if available
        const userProfile = dataroomUser.user_id ? userProfilesMap.get(dataroomUser.user_id) : null;
        
        // Determine full name: priority: metadata > user_profile > email prefix
        let fullName = '';
        if (metadata.first_name && metadata.last_name) {
          fullName = `${metadata.first_name} ${metadata.last_name}`;
        } else if (metadata.first_name) {
          fullName = metadata.first_name;
        } else if (userProfile) {
          // Use profile name if available
          if (userProfile.full_name) {
            fullName = userProfile.full_name;
          } else if (userProfile.first_name && userProfile.last_name) {
            fullName = `${userProfile.first_name} ${userProfile.last_name}`;
          } else if (userProfile.first_name || userProfile.last_name) {
            fullName = userProfile.first_name || userProfile.last_name;
          } else {
            fullName = email.split('@')[0];
          }
        } else {
          fullName = email.split('@')[0];
        }
        
        return {
          id: `${email}-${dataroomUser.project_id}`, // Composite key: email-projectId to handle same user in multiple projects
          email: email,
          projectId: dataroomUser.project_id, // Include project_id for reference
          fullName: fullName,
          firstName: metadata.first_name || userProfile?.first_name || '',
          lastName: metadata.last_name || userProfile?.last_name || '',
          role: dataroomUser.role || 'viewer',
          status: inviteStatus?.status || dataroomUser.status || 'pending',
          invitedAt: inviteStatus?.sentAt || dataroomUser.invited_at,
          acceptedAt: inviteStatus?.acceptedAt,
          lastActive: dataroomUser.last_active,
          avatarUrl: null,
          parties: party ? [{
          id: party.id,
          name: party.name,
          code: party.code,
            color: party.color,
            projectId: party.project_id
          }] : [],
          teams: team ? [{
            id: team.id,
            name: team.name,
            code: team.code,
            partyId: team.party_id
          }] : []
        };
      });

      return { data: users, error: null };
    } catch (error) {
      console.error('Error fetching wizard users:', error);
      return { data: null, error };
    }
  },

  // Create a user in the system (first step of the workflow)
  async createUser(userData) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          email: userData.email,
          first_name: userData.fullName?.split(' ')[0] || '',
          last_name: userData.fullName?.split(' ').slice(1).join(' ') || '',
          role: userData.role || 'viewer'
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating user:', error);
      return { data: null, error };
    }
  },

  // Assign user to dataroom project (second step)
  async assignUserToDataroom(userId, projectId, role = 'viewer') {
    try {
      const { data, error } = await supabase
        .from('dataroom_users')
        .insert({
          user_id: userId,
          project_id: projectId,
          role: role
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error assigning user to dataroom:', error);
      return { data: null, error };
    }
  },

  // Invite user (third step)
  async inviteUser(userId, projectId, customMessage = null) {
    try {
      // This would integrate with the existing UserInvites module
      // For now, we'll just mark the user as invited
      const { data, error } = await supabase
        .from('userinvites_invites')
        .insert({
          user_id: userId,
          project_id: projectId,
          role: 'viewer',
          custom_message: customMessage,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error inviting user:', error);
      return { data: null, error };
    }
  },

  // Complete workflow: create user, assign to dataroom, and invite
  async createAndInviteUser(userData, projectId, customMessage = null) {
    try {
      // Step 1: Create user
      const { data: user, error: createError } = await this.createUser(userData);
      if (createError) throw createError;

      // Step 2: Assign to dataroom
      const { error: assignError } = await this.assignUserToDataroom(user.id, projectId, userData.role);
      if (assignError) throw assignError;

      // Step 3: Send invitation
      const { error: inviteError } = await this.inviteUser(user.id, projectId, customMessage);
      if (inviteError) throw inviteError;

      return { data: user, error: null };
    } catch (error) {
      console.error('Error in create and invite workflow:', error);
      return { data: null, error };
    }
  }
};

export default wizardUsersService;
